
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  booleanAttribute,
  effect,
  forwardRef,
  inject,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { Subscription, shareReplay } from 'rxjs';
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import {
  GalaxyAbstractFilterInput,
  GalaxyFilterValueInterface,
  InnerGalaxyFilterChipInjectionToken,
} from '../interface';

@Component({
  selector: 'glxy-boolean-filter-input',
  template: `
    <glxy-form-field class="filter-string-input" [bottomSpacing]="'none'">
      <mat-radio-group [formControl]="formControl" class="radio-group">
        @for (option of availableOptions(); track option) {
          <mat-radio-button [value]="option">{{ option }}</mat-radio-button>
        }
      </mat-radio-group>
    </glxy-form-field>
    `,
  styleUrls: ['./boolean-filter-input.component.scss'],
  providers: [{ provide: GalaxyAbstractFilterInput, useExisting: forwardRef(() => GalaxyBooleanFilterInputComponent) }],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    GalaxyFormFieldModule,
    MatAutocompleteModule,
    MatRadioModule
],
})
export class GalaxyBooleanFilterInputComponent implements GalaxyAbstractFilterInput, OnDestroy {
  @Input() set fieldId(value: string) {
    this._fieldId.set(value);
  }

  @Output() valueChanges = new EventEmitter<GalaxyFilterValueInterface[]>();
  formControl: FormControl = new FormControl('');

  _fieldId = signal('');
  availableOptions = signal<string[]>(['True', 'False']);
  _initialValue = signal<GalaxyFilterValueInterface[]>([]);

  @Input() set initialValue(value: GalaxyFilterValueInterface[]) {
    this._initialValue.set(value);
  }
  @Input({ transform: booleanAttribute }) set disabled(value: boolean) {
    if (value) {
      this.formControl.disable();
    }
  }

  subscriptions: Subscription[] = [];

  private readonly filtersDependencies = inject(InnerGalaxyFilterChipInjectionToken);

  constructor() {
    effect(() => {
      const initialValue = this._initialValue();
      if (initialValue.length > 0) {
        this.formControl.setValue(initialValue[0].boolean ? 'True' : 'False');
      }
    });
    const fieldId$ = toObservable(this._fieldId);

    this.subscriptions.push(
      this.formControl.valueChanges.pipe(distinctUntilChanged()).subscribe((value) => {
        if (value === 'True') {
          this.valueChanges.emit([{ boolean: true }]);
        } else if (value === 'False') {
          this.valueChanges.emit([{ boolean: false }]);
        }
      }),
      fieldId$
        .pipe(
          filter((fieldId) => fieldId.length > 0),
          switchMap((fieldId) => this.filtersDependencies.getFieldOptions(fieldId, '')),
          shareReplay(1),
        )
        .subscribe((options) => {
          this.availableOptions.set(options);
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  clearValue() {
    this.formControl.reset();
  }
}
